'use client';

import cn from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import styles from './rating.module.css';

interface ReviewRatingProps {
  rate: number;
  onChange?: (rating: number) => void;
  interactive?: boolean;
  size?: 'tiny' | 'small' | 'medium' | 'large';
}

export const ReviewRating: React.FC<ReviewRatingProps> = ({
  rate,
  onChange,
  interactive = false,
  size = 'medium',
}) => {
  const handleStarClick = (starRating: number) => {
    if (interactive && onChange) {
      onChange(starRating);
    }
  };

  const stars = useMemo(() => [1, 2, 3, 4, 5], []);

  // Контейнер для вычисления реальных размеров звезд и gap из CSS
  const containerRef = useRef<HTMLDivElement>(null);
  const [ratingPercentage, setRatingPercentage] = useState<number>(0);

  useEffect(() => {
    // Пытаемся определить реальные размеры из DOM, чтобы точно учесть gap для текущего устройства
    const container = containerRef.current;
    const firstStar = container?.querySelector(`.${styles.starsContainer} .${styles.star}`) as HTMLElement | null;
    const starsContainer = container?.querySelector(`.${styles.starsContainer}`) as HTMLElement | null;

    const fullStars = Math.floor(Math.max(0, Math.min(5, rate)));
    const partial = Math.max(0, Math.min(1, rate - fullStars));

    let starWidth = 0;
    let gapPx = 0;

    if (firstStar && starsContainer) {
      starWidth = firstStar.offsetWidth;
      const gapStr = getComputedStyle(starsContainer).gap || '0px';
      gapPx = Number.parseFloat(gapStr) || 0;
    } else {
      // Фолбэк к известным метрикам из CSS
      const metricsBySize: Record<NonNullable<typeof size>, { star: number; gap: number }> = {
        tiny: { star: 16, gap: 6 },
        small: { star: 25, gap: 3 },
        medium: { star: 20, gap: 2 },
        large: { star: 35, gap: 6 },
      } as const;
      starWidth = metricsBySize[size].star;
      gapPx = metricsBySize[size].gap;
    }

    const totalWidthPx = 5 * starWidth + 4 * gapPx;
    // Количество полностью пройденных промежутков между звездами
    const gapsIncluded = Math.max(0, fullStars - 1) + (partial > 0 ? 1 : 0);
    const filledWidthPx = fullStars * starWidth + gapsIncluded * gapPx + partial * starWidth;
    const pct = totalWidthPx > 0 ? (filledWidthPx / totalWidthPx) * 100 : 0;
    setRatingPercentage(pct);
  }, [rate, size]);

  return (
    <div
      className={cn(styles.reviewRating, styles[size], {
        [styles.interactive]: interactive,
      })}
      ref={containerRef}
    >
      <div className={styles.starsContainer}>
        {stars.map((star) => (
          <div key={star} className={styles.star} />
        ))}
        <div className={styles.starsForeground} style={{ width: `${ratingPercentage}%` }}>
          <div className={styles.starsContainer}>
            {stars.map((star) => (
              <div key={star} className={cn(styles.star, styles.filled)} />
            ))}
          </div>
        </div>
      </div>
      {interactive && (
        <div className={styles.interactiveLayer}>
          {stars.map((star) => (
            <button
              key={star}
              type="button"
              className={styles.interactiveButton}
              onClick={() => handleStarClick(star)}
              aria-label={`Rate ${star} stars`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
